import apiClient from "../api/api";
import { API_ENDPOINTS } from "../api/api.config";

// ─── Types ─────────────────────────────────────────────────────────────────

export interface Campaign {
  _id: string;
  name: string;
  language: string;
  region?: string;
  audience?: { age?: string; gender?: string; segments?: string[] };
  kpis?: { ctr?: number; roas?: number; cpa?: number; impressions?: number; conversions?: number };
  status: "draft" | "approved" | "running" | "completed" | "archived";
  is_gold: boolean;
  document_ids: string[];
  document_store_id: string; // folder this campaign lives in
  reuses_count?: number;
  derived_from_campaign_id?: string;
  source_lang?: string;
  created_by: string;
  createdAt: string;
  updatedAt: string;
}

export interface CampaignFolder {
  _id: string;
  name: string;
  description?: string;
  campaign_count: number;
  language_count: number; // distinct languages of campaigns inside
  updated_at: string;
}

export interface CampaignMetrics {
  total_campaigns: number;
  gold_campaigns: number;
  reuses_this_month: number;
}

export interface CampaignListParams {
  skip?: number;
  limit?: number;
  name?: string;
  language?: string;
  status?: string;
  region?: string;
  document_store_id?: string; // scope to folder
}

export interface CampaignListResponse {
  success: boolean;
  code: number;
  data: Campaign[];
  metrics_data?: CampaignMetrics;
}

export interface SearchHit {
  campaign: Campaign;
  score: number;          // 0..1
  snippet: string;        // matched chunk preview
  matched_chunk_id: string;
  source_lang_label: string; // "Hindi", "English", etc.
}

export interface Citation {
  chunk_id: string;
  page?: number;
  text: string;
}

export interface CampaignInsights {
  performance: {
    runs: { region: string; period: string; kpis: Record<string, string | number> }[];
    best_cta?: string;
    benchmark_comparison?: string;
  };
  audience: {
    source_segments: string[];
    suggested_target_segments: string[];
    rationale: string;
  };
  cultural_notes: { topic: string; note: string; severity: "info" | "warn" }[];
  risks: { type: string; severity: "low" | "medium" | "high"; message: string }[];
  next_steps: string[];
}

export interface Generation {
  _id: string;
  query: string;
  query_language: string;
  target_language: string;
  source_chunk_ids: string[];
  source_campaign_ids: string[];
  draft_text: string;
  draft_paragraphs?: { id: string; text: string; cited_chunk_ids: string[] }[];
  insights: CampaignInsights;
  status: "draft" | "exported" | "discarded";
  createdAt: string;
}

export type ChatActionId =
  | "copy_audience"
  | "copy_draft"
  | "highlight_uncited"
  | "apply_cultural"
  | "view_source"
  | "regenerate_draft";

export interface ChatActionButton {
  id: ChatActionId;
  label: string;
}

export interface ChatMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  citations?: string[];               // chunk ids referenced
  suggestedFollowups?: string[];      // chips shown after this assistant message
  actionButtons?: ChatActionButton[]; // inline action buttons on the AI bubble
  createdAt: string;
}

export const STARTER_QUESTIONS: string[] = [
  "Which previous campaign performed best?",
  "Why is the CTR above benchmark?",
  "How is Chennai audience different from Mumbai?",
  "What tone worked best in Mumbai?",
  "Predict this campaign's success in Chennai",
  "What's the biggest risk if I publish as-is?",
  "Suggest 3 alternative Tamil headlines",
  "Which strategy should I avoid?",
  "Which other regions should run this campaign?",
];

// ─── Mock data (used until backend is in place) ───────────────────────────

const USE_MOCK = true; // flip to false once backend is wired up

// ─── ONE end-to-end example, on purpose ──────────────────────────────────
// The whole demo tells ONE story so the feature is easy to grasp:
//   1. The bank ran a successful Credit Card launch in Mumbai (Hindi).
//   2. The same launch was run in Bangalore (English) and Kochi (Malayalam).
//   3. The Chennai team (Tamil) now needs to localize it for Tamil Nadu.
// The Workspace generates a Tamil draft from the Hindi source, with
// audience / cultural / risk insights tailored to Chennai.

const MOCK_FOLDERS: CampaignFolder[] = [
  {
    _id: "f1",
    name: "Credit Card Launch — Q1 2026",
    description: "Multilingual launch briefs for the new urban credit card across regions",
    campaign_count: 3,
    language_count: 3,
    updated_at: "2026-04-22T18:00:00Z",
  },
];

const MOCK_CAMPAIGNS: Campaign[] = [
  {
    _id: "c1",
    name: "Credit Card Launch — Mumbai",
    language: "hi",
    region: "Mumbai",
    audience: { age: "25-40", gender: "All", segments: ["Salaried Urban", "Mid-upper Income", "First-time Card Users"] },
    kpis: { ctr: 4.2, conversions: 2847, cpa: 218, impressions: 412000 },
    status: "completed",
    is_gold: true,
    document_ids: ["d1", "d2"],
    document_store_id: "f1",
    reuses_count: 4,
    created_by: "marketing-mumbai@acme.com",
    createdAt: "2026-01-12T08:00:00Z",
    updatedAt: "2026-02-22T18:00:00Z",
  },
  {
    _id: "c2",
    name: "Credit Card Launch — Bangalore",
    language: "en",
    region: "Bangalore",
    audience: { age: "22-38", gender: "All", segments: ["Tech Workers", "Salaried Urban"] },
    kpis: { ctr: 3.8, conversions: 1922, cpa: 245, impressions: 318000 },
    status: "completed",
    is_gold: true,
    document_ids: ["d3"],
    document_store_id: "f1",
    reuses_count: 2,
    created_by: "marketing-bangalore@acme.com",
    createdAt: "2026-01-18T08:00:00Z",
    updatedAt: "2026-02-22T18:00:00Z",
  },
  {
    _id: "c3",
    name: "Credit Card Launch — Kochi",
    language: "ml",
    region: "Kochi",
    audience: { age: "25-45", gender: "All", segments: ["Salaried Urban", "Self-employed Professionals"] },
    kpis: { ctr: 3.1, conversions: 1240, cpa: 198 },
    status: "completed",
    is_gold: false,
    document_ids: ["d4"],
    document_store_id: "f1",
    reuses_count: 0,
    created_by: "marketing-kochi@acme.com",
    createdAt: "2026-02-04T08:00:00Z",
    updatedAt: "2026-03-02T18:00:00Z",
  },
];

// One credit-card draft per supported target language. Picking a different
// target in the Workspace dropdown swaps the body text in real time.
const CREDIT_CARD_DRAFTS: Record<string, string> = {
  en: `New SmartReward Credit Card — built for the way you live

Take charge of your spending with the SmartReward Credit Card. [#c1-chunk-1]

🎁 Key benefits:
• First-year fee waived (₹0 annual fee)
• Fuel surcharge waiver at every petrol station
• Complimentary airport lounge access (4 visits per year) [#c1-chunk-2]
• 2% reward points on every category of spend

👨‍👩‍👧 Family perk: Free supplementary card — same benefits for your spouse.

💼 FlexEMI: Convert any purchase above ₹2,500 into easy installments — 3 to 24 months.

✨ "Apply in 5 minutes" — KYC fully online.

T&Cs apply. Approval at the bank's discretion. As per RBI guidelines.`,

  hi: `नया SmartReward क्रेडिट कार्ड — आपकी ज़िंदगी के लिए बना

अपनी ख़र्च करने की क्षमता को नया रूप दीजिए SmartReward क्रेडिट कार्ड के साथ। [#c1-chunk-1]

🎁 प्रमुख फ़ायदे:
• पहले साल का शुल्क माफ़ (₹0 वार्षिक शुल्क)
• हर पेट्रोल पंप पर ईंधन सरचार्ज माफ़
• मुफ़्त एयरपोर्ट लाउंज एक्सेस (साल में 4 बार) [#c1-chunk-2]
• हर श्रेणी पर 2% रिवॉर्ड पॉइंट्स

👨‍👩‍👧 परिवार के लिए: मुफ़्त एड-ऑन कार्ड — आपके जीवनसाथी को भी वही सुविधाएँ।

💼 FlexEMI: ₹2,500 से ऊपर की किसी भी ख़रीदारी को आसान किस्तों में बदलें — 3 से 24 महीने तक।

✨ "5 मिनट में आवेदन करें" — KYC पूरी तरह ऑनलाइन।

नियम व शर्तें लागू। मंज़ूरी बैंक के विवेक पर। RBI दिशानिर्देशों के अनुसार।`,

  ta: `புதிய SmartReward கிரெடிட் கார்டு — உங்கள் வாழ்க்கைக்கு பரந்த சாத்தியங்கள்

இந்தத் தருணம், உங்கள் வாங்கும் சக்தியை மாற்றும் கிரெடிட் கார்டு உங்களுக்காக. [#c1-chunk-1]

🎁 முக்கிய நன்மைகள்:
• முதல் ஆண்டு கட்டண சலுகை (₹0 ஆண்டு கட்டணம்)
• எரிபொருள் சர்சார்ஜ் தள்ளுபடி அனைத்து பெட்ரோல் நிலையங்களிலும்
• இலவச எயர்போர்ட் லவுஞ்ச் அணுகல் (ஆண்டுக்கு 4 முறை) [#c1-chunk-2]
• 2% ரிவார்ட் பாயிண்ட்கள் அனைத்து வகை செலவுகளுக்கும்

👨‍👩‍👧 குடும்ப அம்சம்: துணை அட்டை இலவசம் — உங்கள் துணைவருக்கும் அதே சலுகைகள்.

💼 FlexEMI: ₹2,500-க்கு மேல் கொள்முதலை சுலபமான தவணைகளாக மாற்றுங்கள் — 3 முதல் 24 மாதங்கள் வரை.

✨ "5 நிமிடங்களில் விண்ணப்பிக்கவும்" — KYC முழுவதும் ஆன்லைனில். ("Apply in 5 minutes" CTA)

நிபந்தனைகள் பொருந்தும். ஒப்புதல் வங்கியின் விருப்பத்தைச் சார்ந்தது. RBI பரிந்துரைகளின்படி.`,

  te: `కొత్త SmartReward క్రెడిట్ కార్డ్ — మీ జీవితానికి సరిపోయే కార్డ్

మీ ఖర్చు చేసే శక్తిని మార్చే క్రెడిట్ కార్డ్ ఇప్పుడు మీ చేతిలో. [#c1-chunk-1]

🎁 ప్రధాన ప్రయోజనాలు:
• మొదటి సంవత్సరం రుసుము మినహాయింపు (₹0 వార్షిక రుసుము)
• అన్ని పెట్రోల్ బంకుల వద్ద ఇంధన సర్‌చార్జ్ మినహాయింపు
• ఉచిత ఎయిర్‌పోర్ట్ లాంజ్ యాక్సెస్ (సంవత్సరానికి 4 సార్లు) [#c1-chunk-2]
• ప్రతి విభాగం ఖర్చుపై 2% రివార్డ్ పాయింట్లు

👨‍👩‍👧 కుటుంబ ప్రయోజనం: ఉచిత అనుబంధ కార్డు — మీ జీవిత భాగస్వామికి కూడా అదే ప్రయోజనాలు.

💼 FlexEMI: ₹2,500 పైగా కొనుగోళ్లను సులభమైన వాయిదాలుగా మార్చుకోండి — 3 నుండి 24 నెలల వరకు.

✨ "5 నిమిషాల్లో దరఖాస్తు చేయండి" — KYC పూర్తిగా ఆన్‌లైన్.

నిబంధనలు వర్తిస్తాయి. ఆమోదం బ్యాంకు విచక్షణపై ఆధారపడి ఉంటుంది. RBI మార్గదర్శకాల ప్రకారం.`,

  ml: `പുതിയ SmartReward ക്രെഡിറ്റ് കാർഡ് — നിങ്ങളുടെ ജീവിതത്തിന് ചേർന്ന കാർഡ്

നിങ്ങളുടെ ചെലവ് ശേഷിയെ പുതുക്കുക SmartReward ക്രെഡിറ്റ് കാർഡിലൂടെ. [#c1-chunk-1]

🎁 പ്രധാന നേട്ടങ്ങൾ:
• ആദ്യ വർഷ ഫീസ് ഇല്ല (₹0 വാർഷിക ഫീസ്)
• എല്ലാ പെട്രോൾ പമ്പിലും ഇന്ധന സർചാർജ് ഒഴിവ്
• സൗജന്യ എയർപോർട്ട് ലൗഞ്ച് ആക്സസ് (വർഷത്തിൽ 4 തവണ) [#c1-chunk-2]
• എല്ലാ വിഭാഗ ചെലവുകളിലും 2% റിവാർഡ് പോയിന്റുകൾ

👨‍👩‍👧 കുടുംബ ആനുകൂല്യം: സൗജന്യ അനുബന്ധ കാർഡ് — നിങ്ങളുടെ പങ്കാളിക്കും ഇതേ ആനുകൂല്യങ്ങൾ.

💼 FlexEMI: ₹2,500-ന് മുകളിലുള്ള പർച്ചേസുകൾ എളുപ്പ ഇൻസ്റ്റാൾമെന്റുകളാക്കുക — 3 മുതൽ 24 മാസം വരെ.

✨ "5 മിനിറ്റിൽ അപേക്ഷിക്കുക" — KYC പൂർണ്ണമായും ഓൺലൈൻ.

നിബന്ധനകൾ ബാധകം. അംഗീകാരം ബാങ്കിന്റെ വിവേചനാധികാരം. RBI മാർഗ്ഗനിർദ്ദേശങ്ങൾക്ക് അനുസരണം.`,

  mr: `नवीन SmartReward क्रेडिट कार्ड — तुमच्या जीवनशैलीसाठी

तुमची ख़र्च करण्याची क्षमता वाढवा SmartReward क्रेडिट कार्डसह. [#c1-chunk-1]

🎁 प्रमुख फायदे:
• पहिल्या वर्षाचे शुल्क माफ (₹0 वार्षिक शुल्क)
• प्रत्येक पेट्रोल पंपावर इंधन सरचार्ज माफ
• मोफत विमानतळ लाउंज प्रवेश (वर्षाला 4 वेळा) [#c1-chunk-2]
• प्रत्येक श्रेणीवर 2% रिवॉर्ड पॉइंट्स

👨‍👩‍👧 कुटुंबासाठी: मोफत अतिरिक्त कार्ड — तुमच्या जोडीदारालाही समान फायदे.

💼 FlexEMI: ₹2,500 पेक्षा जास्त खरेदी सोप्या हप्त्यांमध्ये बदला — 3 ते 24 महिने.

✨ "5 मिनिटांत अर्ज करा" — KYC पूर्णपणे ऑनलाइन.

अटी व शर्ती लागू. मंजुरी बँकेच्या निर्णयावर. RBI मार्गदर्शक तत्त्वांनुसार.`,

  bn: `নতুন SmartReward ক্রেডিট কার্ড — আপনার জীবনের সঙ্গে তাল মিলিয়ে

আপনার খরচের ক্ষমতা পাল্টে দিন SmartReward ক্রেডিট কার্ডের সাথে। [#c1-chunk-1]

🎁 প্রধান সুবিধাগুলি:
• প্রথম বছরের ফি মাফ (₹0 বার্ষিক ফি)
• প্রতিটি পেট্রোল পাম্পে ফুয়েল সারচার্জ মাফ
• বিনামূল্যে এয়ারপোর্ট লাউঞ্জ অ্যাক্সেস (বছরে ৪ বার) [#c1-chunk-2]
• প্রতিটি বিভাগের খরচে ২% রিওয়ার্ড পয়েন্ট

👨‍👩‍👧 পরিবারের জন্য: বিনামূল্যে সাপ্লিমেন্টারি কার্ড — আপনার সঙ্গীর জন্যও একই সুবিধা।

💼 FlexEMI: ₹২,৫০০-এর বেশি যেকোনো কেনাকাটা সহজ কিস্তিতে পরিণত করুন — ৩ থেকে ২৪ মাস।

✨ "৫ মিনিটে আবেদন করুন" — KYC সম্পূর্ণ অনলাইন।

শর্তাবলী প্রযোজ্য। অনুমোদন ব্যাঙ্কের বিবেচনার উপর। RBI নির্দেশিকা অনুসারে।`,

  gu: `નવું SmartReward ક્રેડિટ કાર્ડ — તમારા જીવન માટે બનેલું

તમારી ખર્ચ કરવાની ક્ષમતા બદલો SmartReward ક્રેડિટ કાર્ડ સાથે. [#c1-chunk-1]

🎁 મુખ્ય ફાયદાઓ:
• પ્રથમ વર્ષની ફી માફ (₹0 વાર્ષિક ફી)
• દરેક પેટ્રોલ પંપ પર ઇંધણ સરચાર્જ માફ
• મફત એરપોર્ટ લાઉન્જ એક્સેસ (વર્ષે 4 વખત) [#c1-chunk-2]
• દરેક શ્રેણી પર 2% રિવોર્ડ પોઈન્ટ્સ

👨‍👩‍👧 પરિવાર માટે: મફત સપ્લિમેન્ટરી કાર્ડ — તમારા જીવનસાથીને પણ સમાન ફાયદા.

💼 FlexEMI: ₹2,500થી વધુની ખરીદીને સરળ હપ્તાઓમાં બદલો — 3 થી 24 મહિના.

✨ "5 મિનિટમાં અરજી કરો" — KYC સંપૂર્ણપણે ઓનલાઇન.

નિયમો અને શરતો લાગુ. મંજૂરી બેન્કના વિવેક પર. RBI માર્ગદર્શિકાઓ મુજબ.`,

  kn: `ಹೊಸ SmartReward ಕ್ರೆಡಿಟ್ ಕಾರ್ಡ್ — ನಿಮ್ಮ ಜೀವನಕ್ಕಾಗಿ ರಚಿಸಲಾದ ಕಾರ್ಡ್

ನಿಮ್ಮ ಖರ್ಚು ಮಾಡುವ ಸಾಮರ್ಥ್ಯವನ್ನು ಬದಲಿಸಿ SmartReward ಕ್ರೆಡಿಟ್ ಕಾರ್ಡ್‌ನೊಂದಿಗೆ. [#c1-chunk-1]

🎁 ಪ್ರಮುಖ ಪ್ರಯೋಜನಗಳು:
• ಮೊದಲ ವರ್ಷದ ಶುಲ್ಕ ಮಾಫಿ (₹0 ವಾರ್ಷಿಕ ಶುಲ್ಕ)
• ಎಲ್ಲ ಪೆಟ್ರೋಲ್ ಬಂಕ್‌ಗಳಲ್ಲಿ ಇಂಧನ ಸರ್‌ಚಾರ್ಜ್ ಮಾಫಿ
• ಉಚಿತ ವಿಮಾನ ನಿಲ್ದಾಣ ಲಾಂಜ್ ಪ್ರವೇಶ (ವರ್ಷಕ್ಕೆ 4 ಬಾರಿ) [#c1-chunk-2]
• ಪ್ರತಿಯೊಂದು ವರ್ಗದ ಖರ್ಚಿನ ಮೇಲೆ 2% ರಿವಾರ್ಡ್ ಪಾಯಿಂಟ್‌ಗಳು

👨‍👩‍👧 ಕುಟುಂಬಕ್ಕಾಗಿ: ಉಚಿತ ಸಪ್ಲಿಮೆಂಟರಿ ಕಾರ್ಡ್ — ನಿಮ್ಮ ಸಂಗಾತಿಗೂ ಅದೇ ಪ್ರಯೋಜನಗಳು.

💼 FlexEMI: ₹2,500ಕ್ಕಿಂತ ಹೆಚ್ಚಿನ ಯಾವುದೇ ಖರೀದಿಯನ್ನು ಸುಲಭ ಕಂತುಗಳಾಗಿ ಪರಿವರ್ತಿಸಿ — 3 ರಿಂದ 24 ತಿಂಗಳುಗಳವರೆಗೆ.

✨ "5 ನಿಮಿಷಗಳಲ್ಲಿ ಅರ್ಜಿ ಸಲ್ಲಿಸಿ" — KYC ಸಂಪೂರ್ಣವಾಗಿ ಆನ್‌ಲೈನ್.

ನಿಯಮಗಳು ಮತ್ತು ಷರತ್ತುಗಳು ಅನ್ವಯ. ಅನುಮೋದನೆ ಬ್ಯಾಂಕ್‌ನ ವಿವೇಚನೆಗೆ ಒಳಪಟ್ಟಿರುತ್ತದೆ. RBI ಮಾರ್ಗದರ್ಶಿಗಳ ಪ್ರಕಾರ.`,
};

// Insights mirror the ONE story: Mumbai (hi) credit-card launch reused for Chennai (ta).
const MOCK_INSIGHTS: CampaignInsights = {
  performance: {
    runs: [
      { region: "Mumbai",    period: "12 Jan – 22 Feb 2026", kpis: { CTR: "4.2%", Conversions: 2847, CPA: "₹218" } },
      { region: "Bangalore", period: "18 Jan – 22 Feb 2026", kpis: { CTR: "3.8%", Conversions: 1922, CPA: "₹245" } },
    ],
    best_cta: "Apply in 5 minutes",
    benchmark_comparison: "CTR 4.2% — above tenant benchmark of 2.8%",
  },
  audience: {
    source_segments: ["Salaried 25-40", "Tier-1 urban", "First-time card users", "Mid-upper income"],
    suggested_target_segments: [
      "Salaried 25-45",
      "Tier-1 + Tier-2 urban (Chennai, Coimbatore, Madurai)",
      "Self-employed professionals (added — strong TN segment)",
      "Family-influenced decision-makers",
    ],
    rationale: "Tamil Nadu credit-card adoption skews family-influenced and trust-led; CRM data shows higher activation when fuel-surcharge waiver and lounge-access benefits are highlighted, and when self-employed professionals are addressed alongside salaried.",
  },
  cultural_notes: [
    { topic: "Tone & honorifics", note: "Source uses informal Hindi (तू/तुम). Tamil draft uses formal (நீங்கள்) — appropriate for trust-based banking decisions.", severity: "info" },
    { topic: "Reward framing",    note: "Source leads with cashback. In Tamil Nadu, fuel-surcharge waiver and airport-lounge access convert better — draft re-orders the benefits accordingly.", severity: "warn" },
    { topic: "Imagery swap",      note: "Source uses Mumbai skyline / Bandra–Worli sea-link visuals. Replace with Chennai (Marina Beach, IT corridor) for local resonance.", severity: "warn" },
    { topic: "Brand names retained", note: "2 English product names (\"SmartReward Card\", \"FlexEMI\") kept as-is. Flagged for your review.", severity: "info" },
  ],
  risks: [
    { type: "uncited_paragraphs", severity: "medium", message: "2 paragraphs in the draft have no source citation. AI generated them from inference, not from your data. Review before approving." },
    { type: "offer_mismatch",     severity: "low",    message: "Source campaign's joining-fee waiver (1st year free) may not match your current promo calendar — verify with finance before publishing." },
    { type: "regulatory",         severity: "low",    message: "Tamil draft retains the standard RBI disclosure footer. Confirm the latest version is in use." },
  ],
  next_steps: [
    "Run a 5% holdout A/B in Chennai before full scale-up.",
    "Localize visuals — auto-generated visual prompts queued in your image-brief tab.",
    "Coordinate with the Bangalore team — overlapping IT-worker audience, could share creatives.",
    "Schedule launch for the first week of the next pay cycle to align with peak credit-card application intent.",
  ],
};

function delay(ms = 350) { return new Promise(r => setTimeout(r, ms)); }

// ─── Service ──────────────────────────────────────────────────────────────

class CampaignsService {
  /** List campaign folders the user has access to. */
  async listFolders(): Promise<CampaignFolder[]> {
    if (USE_MOCK) {
      await delay();
      // Recompute counts each call so they stay in sync with mock campaigns
      return MOCK_FOLDERS.map(f => {
        const items = MOCK_CAMPAIGNS.filter(c => c.document_store_id === f._id);
        return {
          ...f,
          campaign_count: items.length,
          language_count: new Set(items.map(c => c.language)).size,
        };
      });
    }
    // Reuses the existing document-stores endpoint since campaigns live inside doc folders
    const res = await apiClient.post(API_ENDPOINTS.DOCUMENT_STORES.LIST, { skip: 0, limit: 100 });
    return res.data?.data?.documentStores || [];
  }

  async listCampaigns(params: CampaignListParams = {}): Promise<CampaignListResponse> {
    if (USE_MOCK) {
      await delay();
      const skip = params.skip ?? 0;
      const limit = params.limit ?? 10;
      let pool = [...MOCK_CAMPAIGNS];
      if (params.document_store_id) pool = pool.filter(c => c.document_store_id === params.document_store_id);
      if (params.name) pool = pool.filter(c => c.name.toLowerCase().includes(params.name!.toLowerCase()));
      if (params.language) pool = pool.filter(c => c.language === params.language);
      if (params.status) pool = pool.filter(c => c.status === params.status);
      if (params.region) pool = pool.filter(c => (c.region || "").toLowerCase().includes(params.region!.toLowerCase()));
      const slice = pool.slice(skip, skip + limit);
      // Metrics are computed within the folder scope (so each folder shows ITS numbers)
      const scope = params.document_store_id
        ? MOCK_CAMPAIGNS.filter(c => c.document_store_id === params.document_store_id)
        : MOCK_CAMPAIGNS;
      return {
        success: true, code: 200, data: slice,
        metrics_data: {
          total_campaigns: scope.length,
          gold_campaigns: scope.filter(c => c.is_gold).length,
          reuses_this_month: scope.reduce((sum, c) => sum + (c.reuses_count || 0), 0),
        },
      };
    }
    const res = await apiClient.post(API_ENDPOINTS.CAMPAIGNS.LIST, params);
    return res.data;
  }

  async approveCampaign(id: string): Promise<Campaign> {
    if (USE_MOCK) {
      await delay();
      const c = MOCK_CAMPAIGNS.find(c => c._id === id)!;
      c.is_gold = true;
      return c;
    }
    const res = await apiClient.post(API_ENDPOINTS.CAMPAIGNS.APPROVE(id));
    return res.data?.data;
  }

  async deleteCampaign(id: string): Promise<void> {
    if (USE_MOCK) { await delay(); return; }
    await apiClient.delete(API_ENDPOINTS.CAMPAIGNS.DELETE(id));
  }

  async search(query: string, target_language: string, top_k = 5, document_store_id?: string): Promise<SearchHit[]> {
    if (USE_MOCK) {
      await delay(600);
      const pool = document_store_id
        ? MOCK_CAMPAIGNS.filter(c => c.document_store_id === document_store_id)
        : MOCK_CAMPAIGNS;
      // Mock cross-lingual ranking — score is faked but ordering is plausible
      const scoreFor = (c: Campaign) => {
        const q = query.toLowerCase();
        let s = 0.55;
        if (c.name.toLowerCase().includes(q)) s += 0.2;
        if (c.language === target_language) s += 0.05;
        if (c.is_gold) s += 0.1;
        if ((c.reuses_count || 0) > 2) s += 0.05;
        // Cross-lingual demo boost — query mentions "credit card" in any of the demo languages
        if (q.match(/credit card|कार्ड|க்ரெடிட்|കാർഡ്/i) && c.name.toLowerCase().includes("credit card")) {
          s += 0.2;
        }
        return Math.min(0.99, s + Math.random() * 0.05);
      };
      const langLabel: Record<string, string> = { en: "English", hi: "Hindi", ta: "Tamil", te: "Telugu", ml: "Malayalam", mr: "Marathi", bn: "Bengali" };
      const hits: SearchHit[] = pool
        .map(c => ({
          campaign: c,
          score: scoreFor(c),
          snippet: `…${c.name} — ${c.audience?.segments?.join(", ") ?? "general audience"}…`,
          matched_chunk_id: `${c._id}-chunk-1`,
          source_lang_label: langLabel[c.language] ?? c.language,
        }))
        .sort((a, b) => b.score - a.score)
        .slice(0, top_k);
      return hits;
    }
    const res = await apiClient.post(API_ENDPOINTS.CAMPAIGNS.SEARCH, { query, target_language, top_k });
    return res.data?.data;
  }

  async generate(payload: {
    query: string;
    target_language: string;
    source_campaign_ids: string[];
  }): Promise<Generation> {
    if (USE_MOCK) {
      await delay(900);
      // Pick the draft matching the target language; fall back to English.
      const draft = CREDIT_CARD_DRAFTS[payload.target_language] || CREDIT_CARD_DRAFTS.en;
      const paras = draft.split("\n\n");

      return {
        _id: "g-" + Date.now(),
        query: payload.query,
        query_language: "auto",
        target_language: payload.target_language,
        source_chunk_ids: ["c1-chunk-1", "c1-chunk-2"],
        source_campaign_ids: payload.source_campaign_ids,
        draft_text: draft,
        draft_paragraphs: [
          { id: "p1", text: paras[0] || "", cited_chunk_ids: ["c1-chunk-1"] },
          { id: "p2", text: paras[1] || "", cited_chunk_ids: ["c1-chunk-2"] },
          { id: "p3", text: paras[2] || "", cited_chunk_ids: [] },
          { id: "p4", text: paras[3] || "", cited_chunk_ids: [] },
        ],
        insights: MOCK_INSIGHTS,
        status: "draft",
        createdAt: new Date().toISOString(),
      };
    }
    const res = await apiClient.post(API_ENDPOINTS.CAMPAIGNS.GENERATE, payload);
    return res.data?.data;
  }

  async getCampaign(id: string): Promise<Campaign | undefined> {
    if (USE_MOCK) { await delay(); return MOCK_CAMPAIGNS.find(c => c._id === id); }
    const res = await apiClient.get(API_ENDPOINTS.CAMPAIGNS.GET_BY_ID(id));
    return res.data?.data;
  }

  /**
   * Free-form chat against the source campaign + draft. In mock mode this
   * matches the question against a small bank of canned responses; in real
   * mode it would hit a RAG-backed `/campaigns/ask` endpoint.
   */
  async ask(question: string, _sourceCampaignId: string, _targetLang: string): Promise<ChatMessage> {
    if (USE_MOCK) {
      await delay(700);
      const matched = QA_BANK.find((q) => q.match.test(question));
      const a = matched ? matched.build() : FALLBACK_ANSWER;
      return {
        id: "m-" + Date.now(),
        role: "assistant",
        content: a.content,
        citations: a.citations,
        suggestedFollowups: a.suggestedFollowups,
        actionButtons: a.actionButtons,
        createdAt: new Date().toISOString(),
      };
    }
    // Backend route to be wired in Phase 3 — see docs/MULTILINGUAL-CAMPAIGN-INTELLIGENCE.md §8.
    const res = await apiClient.post("/campaigns/ask", {
      question, source_campaign_id: _sourceCampaignId, target_language: _targetLang,
    });
    return res.data?.data;
  }
}

// ─── Mock chat answer bank ────────────────────────────────────────────────
// Each entry: a regex matching the user's question and a canned response.
// Order matters — first match wins.
type CannedAnswer = {
  content: string;
  citations?: string[];
  suggestedFollowups?: string[];
  actionButtons?: ChatActionButton[];
};

const QA_BANK: Array<{ match: RegExp; build: () => CannedAnswer }> = [
  {
    match: /summar(y|ize|ise)|tl;?dr|brief overview/i,
    build: () => ({
      content:
        "**SmartReward Credit Card — quick summary:**\n\n" +
        "• Aimed at salaried 25-40 in Tier-1 cities, first-time card users.\n" +
        "• Lead benefits: ₹0 first-year fee, fuel-surcharge waiver, 4 free lounge visits/year, 2% rewards on every spend.\n" +
        "• Mumbai (Hindi) run: CTR 4.2%, 2,847 conversions, CPA ₹218 — well above the 2.8% tenant benchmark.",
      citations: ["c1-chunk-1", "c1-chunk-2"],
      suggestedFollowups: [
        "Why is the CTR above benchmark?",
        "Compare Mumbai vs Bangalore performance",
        "What's the biggest risk if I publish as-is?",
      ],
    }),
  },
  {
    match: /(ctr|click[- ]?through|benchmark|why.*perform|how.*perform)/i,
    build: () => ({
      content:
        "**Why CTR (4.2%) is above benchmark (2.8%):**\n\n" +
        "• The \"Apply in 5 minutes\" CTA tested as the strongest variant in Mumbai — concrete, time-bounded, verb-first.\n" +
        "• The lead benefit (first-year fee waiver, ₹0) directly addresses the #1 objection from first-time card users.\n" +
        "• Bangalore (English) repeated the same CTA pattern and hit 3.8% — still above benchmark, suggesting the structure travels across languages.",
      citations: ["c1-chunk-1"],
      suggestedFollowups: [
        "Will the same CTA work in Tamil?",
        "What if I drop the lounge benefit?",
        "How does CPA compare across regions?",
      ],
      actionButtons: [{ id: "view_source", label: "View original report" }],
    }),
  },
  {
    match: /(risk|publish|safe|launch|legal|compliance|uncited|show.*uncited)/i,
    build: () => ({
      content:
        "**Top risks before publishing the Tamil draft:**\n\n" +
        "1. **Uncited paragraphs** *(medium)* — 2 paragraphs in the draft were generated by inference, not from the source. Highlight them and review.\n" +
        "2. **Joining-fee waiver** *(low)* — the source promises 1st-year free; verify this is still active in your current promo calendar.\n" +
        "3. **RBI footer** *(low)* — Tamil draft uses the standard footer; confirm it's the latest version before legal sign-off.",
      suggestedFollowups: [
        "What should the latest RBI footer say?",
        "Can I A/B test before full scale?",
        "Which paragraph has the highest risk?",
      ],
      actionButtons: [{ id: "highlight_uncited", label: "Highlight uncited in draft" }],
    }),
  },
  {
    match: /(audience|segment|chennai|mumbai|tier|target)/i,
    build: () => ({
      content:
        "**Mumbai → Chennai audience differences:**\n\n" +
        "• **Source (Mumbai):** Salaried 25-40, Tier-1 urban, first-time card users, mid-upper income.\n" +
        "• **Suggested target (Chennai):** same core + add **self-employed professionals** (a large TN segment, often underserved by salaried-only briefs) and broaden to Tier-2 (Coimbatore, Madurai).\n" +
        "• Family-influenced decision-making is stronger in Tamil Nadu — keep the supplementary-card line prominent in the draft.",
      citations: ["c1-chunk-1"],
      suggestedFollowups: [
        "How big is the self-employed segment in TN?",
        "Should I narrow the age band to 30-45?",
        "What tone worked best in Mumbai?",
      ],
      actionButtons: [{ id: "copy_audience", label: "Copy audience segments" }],
    }),
  },
  {
    match: /(headline|title|alternative|variant|3 options|3 versions)/i,
    build: () => ({
      content:
        "**3 alternative headline options for the Tamil draft:**\n\n" +
        "1. *உங்கள் முதல் கிரெடிட் கார்டு — 5 நிமிடங்களில்* (Your first credit card — in 5 minutes) — leans on the speed-to-onboard angle.\n" +
        "2. *முதல் ஆண்டு கட்டணம் இல்லை. நிபந்தனைகள் இல்லை.* (No first-year fee. No conditions.) — leans on the headline benefit.\n" +
        "3. *எரிபொருள், எயர்போர்ட், ரிவார்ட்ஸ் — அனைத்தும் ஒரு கார்டில்* (Fuel, airport, rewards — all on one card) — leans on the bundled benefits.",
      suggestedFollowups: [
        "Which headline is most likely to test well?",
        "Try a more conservative tone",
        "Generate Hindi versions of these",
      ],
    }),
  },
  {
    match: /(region|expand|reuse|other.*team|other.*region|where.*next)/i,
    build: () => ({
      content:
        "**Regions where this campaign is most likely to repeat its Mumbai success:**\n\n" +
        "• **Hyderabad (Telugu)** — very similar urban-salaried profile to Mumbai. High likelihood of comparable CTR.\n" +
        "• **Pune (Marathi)** — smaller market but overlapping Tier-1 audience. Worth a 5% test before scale.\n" +
        "• **Kolkata (Bengali)** — different sweet spot (Tier-1 + Tier-2 mix); the lounge benefit needs to be repositioned for that audience.",
      suggestedFollowups: [
        "Which region should I run first?",
        "What's the audience size in Hyderabad?",
        "Can I run all three in parallel?",
      ],
    }),
  },
  {
    match: /(\bcta\b|call to action|apply in 5)/i,
    build: () => ({
      content:
        "**Why \"Apply in 5 minutes\" works:**\n\n" +
        "• It's verb-first (action) + time-bounded (specific number) — both tested predictors of higher CTR.\n" +
        "• The implicit promise of online KYC removes the \"will I have to visit a branch?\" objection.\n" +
        "• In Mumbai it tested 1.8× higher than the next variant (\"Get yours today\").",
      citations: ["c1-chunk-1"],
      suggestedFollowups: [
        "Should the Tamil version keep \"5 minutes\" verbatim?",
        "Try a CTA that emphasizes rewards",
        "What's the legal disclosure burden for time-bound claims?",
      ],
    }),
  },
  {
    match: /(citation|cited|chunk|source.*from|where.*from)/i,
    build: () => ({
      content:
        "**How citations work in this draft:**\n\n" +
        "• Inline `[#c1-chunk-1]` markers point to specific chunks of the original Mumbai PDF.\n" +
        "• Two source chunks are referenced in this generation: the lead benefit list and the lounge/EMI section.\n" +
        "• Paragraphs **without** a `[#chunk]` marker are AI inference — review them before publishing.",
      citations: ["c1-chunk-1", "c1-chunk-2"],
      suggestedFollowups: [
        "Show me the uncited paragraphs",
        "Open the original Mumbai brief",
        "Can I regenerate just the uncited parts?",
      ],
    }),
  },
  {
    match: /(performed best|best.*campaign|top.*campaign|which.*previous)/i,
    build: () => ({
      content:
        "**Top-performing campaigns in this folder (Credit Card Launch — Q1 2026):**\n\n" +
        "1. **Mumbai (Hindi)** — CTR **4.2%**, 2,847 conversions, CPA ₹218. Gold-approved, 4 reuses. Strongest performer.\n" +
        "2. **Bangalore (English)** — CTR **3.8%**, 1,922 conversions, CPA ₹245. Same CTA pattern; gold-approved.\n" +
        "3. **Kochi (Malayalam)** — CTR 3.1%, 1,240 conversions, CPA ₹198. Smaller scale, not yet gold.\n\n" +
        "Mumbai is the strongest base for a Chennai reuse — similar urban-salaried profile, plus the highest validated CTA.",
      citations: ["c1-chunk-1"],
      suggestedFollowups: [
        "Compare Mumbai and Bangalore in detail",
        "Why didn't Kochi perform as well?",
        "Predict Chennai success based on these",
      ],
    }),
  },
  {
    match: /(predict|likelihood|probability|success.*chance|will.*work|chance.*of.*success)/i,
    build: () => ({
      content:
        "**Predicted success in Chennai — based on past runs:**\n\n" +
        "• **Likely CTR range:** 3.6% – 4.4% (vs tenant benchmark 2.8%).\n" +
        "• **Confidence:** *medium-high* — Mumbai and Bangalore both cleared the benchmark on similar urban-salaried audiences.\n" +
        "• **Drivers favouring Chennai:** strong self-employed segment (often underserved), formal tone in Tamil suits banking trust signals, fuel-surcharge + lounge benefits resonate locally.\n" +
        "• **Headwinds:** less data on Tier-2 Tamil markets; family-influenced decision cycles may extend time-to-conversion.\n" +
        "• **Suggested action:** run a 5% holdout A/B before full scale-up.",
      suggestedFollowups: [
        "What if I skip the holdout test?",
        "Which audience segment is highest risk?",
        "Show me the underlying numbers",
      ],
    }),
  },
  {
    match: /(tone|voice|formality|register|style)/i,
    build: () => ({
      content:
        "**What tone worked best — and what to use in Tamil:**\n\n" +
        "• **Mumbai (Hindi):** semi-formal, action-oriented — informal honorifics (तू/तुम), short sentences, benefit-first headlines. Tested well on first-time card users.\n" +
        "• **Bangalore (English):** professional but warm — \"Apply in 5 minutes\" CTA was the unifying winner.\n" +
        "• **Recommended for Tamil:** **formal honorifics** (நீங்கள்) for trust-based banking, retain the short-sentence rhythm, lead with concrete benefits (fee waiver, fuel surcharge, lounge), keep brand product names (`SmartReward`, `FlexEMI`) untranslated.",
      suggestedFollowups: [
        "Try a more conservative tone",
        "Generate an aggressive variant",
        "Why formal Tamil over informal?",
      ],
    }),
  },
  {
    match: /(avoid|don.?t|mistake|what.*not|pitfall|trap)/i,
    build: () => ({
      content:
        "**Strategies to avoid for the Chennai run:**\n\n" +
        "1. **Don't copy the Mumbai informal tone verbatim.** Banking decisions in TN respond better to formal Tamil (நீங்கள்), not the source's तू/तुम register.\n" +
        "2. **Don't lead with cashback.** Source ordered cashback first; for Chennai, lead with fuel-surcharge waiver + lounge access — those convert better.\n" +
        "3. **Don't keep the Mumbai skyline imagery.** Swap to Chennai-relevant visuals (Marina, IT corridor) before publishing.\n" +
        "4. **Don't translate brand names** (`SmartReward`, `FlexEMI`) — keep them as-is.\n" +
        "5. **Don't skip the 5% holdout test** — early signal before full scale-up.",
      suggestedFollowups: [
        "Why does informal Tamil hurt banking conversion?",
        "Suggest Chennai-specific imagery prompts",
        "What's the right size for the holdout test?",
      ],
    }),
  },
  {
    match: /(engagement|response|react|interact)/i,
    build: () => ({
      content:
        "**Engagement signals from the source runs:**\n\n" +
        "• **Mumbai:** 4.2% CTR; the \"Apply in 5 minutes\" CTA tested 1.8× higher than \"Get yours today\". Lounge benefit drove 22% of clicks.\n" +
        "• **Bangalore:** 3.8% CTR; tech-worker audience over-indexed on EMI conversion language.\n" +
        "• **Pattern across both:** short-form headlines (≤ 8 words) consistently out-performed longer variants.",
      suggestedFollowups: [
        "Suggest 3 short-form Tamil headlines",
        "Where did engagement drop off?",
        "Which channel performed best?",
      ],
    }),
  },
];

const FALLBACK_ANSWER: CannedAnswer = {
  content:
    "I'd need a bit more context to answer that precisely. Try asking about:\n\n" +
    "• **Performance** — CTR, CPA, benchmarks\n" +
    "• **Audience** — Mumbai vs Chennai differences\n" +
    "• **Risks** before publishing — uncited paragraphs, offer mismatches\n" +
    "• **Alternative headlines** or CTAs\n" +
    "• **Other regions** to expand to",
  suggestedFollowups: [
    "Summarize this campaign",
    "Why is CTR above benchmark?",
    "What's the biggest risk?",
  ],
};

export const campaignsService = new CampaignsService();
export default campaignsService;
